import React, { useEffect, useState } from "react";
import { ShieldAlert, TrendingUp, Sparkles, ArrowRight, RefreshCw, Brain, Loader2 } from "lucide-react";
import { PageHeader, SectionCard, RagBadge } from "@/components/Shared";
import { riskMatrix, keyRisks, valueOpportunities, upsideSummary } from "@/data/mineData";
import { api } from "@/lib/api";

const ringColors = {
  green: "stroke-emerald-500",
  amber: "stroke-amber-500",
  red: "stroke-red-500",
};

function AiInsightsPanel() {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchInsights = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/insights/ai");
      setInsight(data);
    } catch (e) {
      setError("Could not generate insights. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, []);

  return (
    <SectionCard
      title="AI-informed Insights"
      description="Generated by Claude Sonnet 4.5 on the live data snapshot"
      action={
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold uppercase tracking-wider">
            <Brain size={12} /> Claude · 4.5
          </span>
          <button
            onClick={fetchInsights}
            disabled={loading}
            data-testid="ai-regenerate-button"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 px-2.5 py-1 rounded-md hover:bg-slate-100 disabled:opacity-50"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            Regenerate
          </button>
        </div>
      }
    >
      {loading && !insight && (
        <div className="flex items-center justify-center py-12 text-sm text-slate-500" data-testid="ai-loading">
          <Loader2 size={18} className="animate-spin mr-2" /> Analysing operational + financial signals…
        </div>
      )}

      {error && !insight && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>
      )}

      {insight && (
        <div data-testid="ai-insights-content" className="space-y-5">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-md p-5 text-white relative overflow-hidden">
            <div className="absolute right-3 top-3 opacity-20">
              <Brain size={48} className="text-amber-400" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 font-semibold mb-2">
              Executive AI Summary
            </div>
            <p className="text-sm leading-relaxed text-slate-100 max-w-3xl">{insight.summary}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <InsightColumn
              title="Risk signals"
              icon={ShieldAlert}
              accent="text-red-600"
              items={insight.risk_signals}
              testid="ai-risk-signals"
            />
            <InsightColumn
              title="Trend analysis"
              icon={TrendingUp}
              accent="text-blue-700"
              items={insight.trend_analysis}
              testid="ai-trend-analysis"
            />
            <InsightColumn
              title="Recommendations"
              icon={Sparkles}
              accent="text-amber-700"
              items={insight.recommendations}
              testid="ai-recommendations"
            />
          </div>

          <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-3">
            Generated {new Date(insight.generated_at).toLocaleString()} · refresh to re-run on the latest snapshot
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function InsightColumn({ title, icon: Icon, accent, items, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-md p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className={accent} />
        <h4 className="font-display text-sm font-semibold text-slate-900">{title}</h4>
      </div>
      <ul className="space-y-2.5">
        {(items || []).map((it, i) => (
          <li key={i} className="text-xs text-slate-700 leading-relaxed flex gap-2">
            <span className={`mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0 ${accent.replace("text-", "bg-")}`} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function RiskInsights() {
  return (
    <div data-testid="page-risk">
      <PageHeader eyebrow="Risk & Insights" title="Risk Posture & Value Opportunities" subtitle="Consolidated risk view + AI-informed deep insights and trend analysis." />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        {riskMatrix.map((r) => (
          <div
            key={r.category}
            data-testid={`risk-${r.category.toLowerCase()}`}
            className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                {r.category} risk
              </div>
              <RagBadge level={r.level} />
            </div>
            <div className="relative h-20 w-20 mx-auto mb-3">
              <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f1f5f9" strokeWidth="3" />
                <circle
                  cx="18"
                  cy="18"
                  r="15.9"
                  fill="none"
                  className={ringColors[r.level]}
                  strokeWidth="3"
                  strokeDasharray={`${r.score * 10} 100`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="font-display text-xl font-bold text-slate-900">{r.score}</div>
                <div className="text-[9px] uppercase tracking-wider text-slate-500">/ 10</div>
              </div>
            </div>
            <p className="text-xs text-slate-600 text-center leading-relaxed">{r.note}</p>
          </div>
        ))}
      </div>

      {/* AI INSIGHTS PANEL */}
      <div className="mb-6">
        <AiInsightsPanel />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
        <SectionCard title="Key Risks" description="Items requiring attention before close" action={<ShieldAlert size={16} className="text-red-600" />}>
          <ul className="space-y-3">
            {keyRisks.map((r, i) => (
              <li key={i} className="flex items-start gap-3 p-3 rounded-md bg-red-50/40 border border-red-100" data-testid={`key-risk-${i}`}>
                <span className="h-6 w-6 rounded-full bg-red-100 text-red-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </span>
                <span className="text-sm text-slate-800 leading-relaxed">{r}</span>
              </li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard title="Value Opportunities" description="Upside levers identified during diligence" action={<Sparkles size={16} className="text-amber-600" />}>
          <ul className="space-y-3">
            {valueOpportunities.map((v, i) => (
              <li key={i} className="flex items-start gap-3 p-3 rounded-md bg-amber-50/40 border border-amber-100" data-testid={`opportunity-${i}`}>
                <span className="h-6 w-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                  +
                </span>
                <span className="text-sm text-slate-800 leading-relaxed">{v}</span>
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>

      <div className="bg-slate-900 rounded-lg p-6 md:p-8 text-white relative overflow-hidden grain">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-center">
          <div className="xl:col-span-2">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-400 mb-2">
              Potential upside
            </div>
            <h3 className="font-display text-3xl font-semibold tracking-tight">
              From <span className="line-through text-slate-500">{upsideSummary.baseCase}</span> base case to{" "}
              <span className="text-amber-400">{upsideSummary.upsideCase}</span> upside.
            </h3>
            <p className="text-sm text-slate-300 mt-3 leading-relaxed max-w-2xl">{upsideSummary.rationale}</p>
          </div>
          <div className="flex items-center justify-end gap-4">
            <TrendingUp size={56} className="text-amber-400" />
            <ArrowRight size={32} className="text-slate-500" />
          </div>
        </div>
      </div>
    </div>
  );
}
